import { CommonResponse } from './categories';

/**
 * Interface for timezone data
 */
export interface Timezone {
  name: string;
  offset: string;
  locations: string;
}

/**
 * Interface for music settings
 */
export interface Music {
  id: string;
  name: string;
  category: string;
  music_file: string;
  created_at: string;
  isPlaying?: boolean;
}

/**
 * Interface for confetti settings
 */
export interface ConfettiSettings {
  shapes: {
    circle: boolean;
    square: boolean;
    star: boolean;
    heart: boolean;
    emoji: boolean;
  };
  colors: string[];
  style: string;
}

/**
 * Interface for card detail
 */
export interface CardDetail {
  card_image: string;
  category: string;
  id: string;
  is_active: boolean;
  is_staff_pick: boolean;
  name: string;
  position: number;
  description : string| null
  slug?: string;
  is_new?: boolean;
  // Returned by the card-by-slug endpoint for the breadcrumb
  category_name?: string;
  category_title?: string;
  category_slug?: string;
  master_category_name?: string | null;
  master_category_slug?: string | null;
}

/**
 * Interface for card detail create response
 */
export interface CardDetailCreateResponse extends CommonResponse {
  response_data: CardDetail;
  /** HTTP status of a failed request, so callers can tell "not found" (404) from a transient error */
  http_status?: number;
}

/**
 * Interface for group card creation payload
 */
export interface GroupCardCreatePayload {
  category: string;
  card: string;
  card_title: string;
  receiver_name: string;
  receiver_email: string;
  sender_name: string;
  card_image: string;
  is_scheduled: boolean;
  scheduled_date: string | null;
  scheduled_time: string | null;
  user_timezone: string | null;
  confetti_settings: ConfettiSettings | null;
  envelope_settings: any | null;
  custom_logo: string | null;
  music_settings: Music | null;
  card_type: string;
}

/**
 * Array of timezone data
 */
export const UTC_TIME_ZONES: Timezone[] = [
  { name: "Pacific/Baker", offset: "-12:00", locations: "Pacific/Baker, Pacific/Howland" },
  { name: "Pacific/Pago_Pago", offset: "-11:00", locations: "Pacific/Pago_Pago, Pacific/Niue" },
  { name: "Pacific/Honolulu", offset: "-10:00", locations: "Pacific/Honolulu, Pacific/Rarotonga" },
  { name: "Pacific/Marquesas", offset: "-09:30", locations: "Pacific/Marquesas" },
  { name: "America/Anchorage", offset: "-09:00", locations: "America/Anchorage, Pacific/Gambier" },
  { name: "America/Los_Angeles", offset: "-08:00", locations: "America/Los_Angeles, America/Vancouver" },
  { name: "America/Denver", offset: "-07:00", locations: "America/Denver, America/Edmonton" },
  { name: "America/Chicago", offset: "-06:00", locations: "America/Chicago, America/Mexico_City" },
  { name: "America/New_York", offset: "-05:00", locations: "America/New_York, America/Toronto" },
  { name: "America/Halifax", offset: "-04:00", locations: "America/Halifax, America/Barbados" },
  { name: "America/St_Johns", offset: "-03:30", locations: "America/St_Johns" },
  { name: "America/Sao_Paulo", offset: "-03:00", locations: "America/Sao_Paulo, America/Buenos_Aires" },
  { name: "Atlantic/South_Georgia", offset: "-02:00", locations: "Atlantic/South_Georgia, Atlantic/Mid" },
  { name: "Atlantic/Azores", offset: "-01:00", locations: "Atlantic/Azores, Atlantic/Cape_Verde" },
  { name: "Europe/London", offset: "+00:00", locations: "Europe/London, Africa/Accra, Atlantic/Reykjavik" },
  { name: "Europe/Paris", offset: "+01:00", locations: "Europe/Paris, Africa/Lagos" },
  { name: "Europe/Helsinki", offset: "+02:00", locations: "Europe/Helsinki, Africa/Cairo, Africa/Johannesburg" },
  { name: "Europe/Moscow", offset: "+03:00", locations: "Europe/Moscow, Asia/Riyadh, Africa/Nairobi" },
  { name: "Asia/Tehran", offset: "+03:30", locations: "Asia/Tehran" },
  { name: "Asia/Dubai", offset: "+04:00", locations: "Asia/Dubai, Asia/Baku, Asia/Tbilisi" },
  { name: "Asia/Kabul", offset: "+04:30", locations: "Asia/Kabul" },
  { name: "Asia/Karachi", offset: "+05:00", locations: "Asia/Karachi, Asia/Tashkent" },
  { name: "Asia/Calcutta", offset: "+05:30", locations: "Asia/Calcutta, Asia/Colombo" },
  { name: "Asia/Kathmandu", offset: "+05:45", locations: "Asia/Kathmandu" },
  { name: "Asia/Dhaka", offset: "+06:00", locations: "Asia/Dhaka, Asia/Thimphu" },
  { name: "Indian/Cocos", offset: "+06:30", locations: "Indian/Cocos, Asia/Yangon" },
  { name: "Asia/Bangkok", offset: "+07:00", locations: "Asia/Bangkok, Asia/Ho_Chi_Minh, Asia/Jakarta" },
  { name: "Asia/Shanghai", offset: "+08:00", locations: "Asia/Shanghai, Asia/Singapore, Australia/Perth" },
  { name: "Australia/Eucla", offset: "+08:45", locations: "Australia/Eucla" },
  { name: "Asia/Tokyo", offset: "+09:00", locations: "Asia/Tokyo, Asia/Seoul" },
  { name: "Australia/Adelaide", offset: "+09:30", locations: "Australia/Adelaide" },
  { name: "Australia/Sydney", offset: "+10:00", locations: "Australia/Sydney, Pacific/Port_Moresby" },
  { name: "Australia/Lord_Howe", offset: "+10:30", locations: "Australia/Lord_Howe" },
  { name: "Pacific/Guadalcanal", offset: "+11:00", locations: "Pacific/Guadalcanal, Pacific/Efate" },
  { name: "Pacific/Fiji", offset: "+12:00", locations: "Pacific/Fiji, Pacific/Auckland" },
  { name: "Pacific/Chatham", offset: "+12:45", locations: "Pacific/Chatham" },
  { name: "Pacific/Tongatapu", offset: "+13:00", locations: "Pacific/Tongatapu, Pacific/Apia" },
  { name: "Pacific/Kiritimati", offset: "+14:00", locations: "Pacific/Kiritimati" },
];

/**
 * Array of confetti colors
 */
export const CONFETTI_COLORS = [
  '#FF6F61', // Coral
  '#6B5B95', // Purple
  '#88B04B', // Green
  '#F7CAC9', // Light Pink
  '#92A8D1'  // Soft Blue
];
